import React, { useState, useContext, useEffect, useCallback } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import Navigation from '../components/Navigation';
import TutorialHint from '../components/TutorialHint';
import { formatLocalDate } from '../utils/date';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { Heart, BookOpen, Users, Sparkles, Save, History, Calendar as CalendarIcon, ExternalLink, Church, TrendingUp, Download } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const DailyJournal = () => {
  const { API } = useContext(AuthContext);
  const today = new Date().toISOString().split('T')[0];
  
  const [entries, setEntries] = useState({
    pursue_god: '',
    engage_scripture: '',
    serve_others: '',
    radiate_jesus: ''
  });
  
  const [saving, setSaving] = useState(null);
  const [viewMode, setViewMode] = useState('new'); // 'new' or 'history'
  const [pastEntries, setPastEntries] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Weekly Check-in state
  const [activeMainTab, setActiveMainTab] = useState('daily'); // 'checkin' or 'daily'
  const getMonday = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(today.setDate(diff));
    return monday.toISOString().split('T')[0];
  };
  const [weekStart] = useState(getMonday());
  const [ratings, setRatings] = useState({
    pursue_god_rating: 5,
    engage_scripture_rating: 5,
    serve_others_rating: 5,
    radiate_jesus_rating: 5
  });
  const [checkinNotes, setCheckinNotes] = useState('');
  const [savingCheckin, setSavingCheckin] = useState(false);
  const [loadingCheckin, setLoadingCheckin] = useState(true);

  const vitalSigns = [
    {
      key: 'pursue_god',
      title: 'Pursue God',
      icon: Heart,
      color: '#2B6B7F',
      description: 'God is triune. Jesus breaks the bond of sin in our lives and thus, we can now be filled with the Holy Spirit, who is constantly making us more into the Holy Image of Jesus. Therefore, we can communicate with God the Father through a life saturated in prayer.',
      questions: [
        'How have I been communicating with God throughout the day?',
        'How has God been communicating with me?'
      ]
    },
    {
      key: 'engage_scripture',
      title: 'Engage Scripture',
      icon: BookOpen,
      color: '#5FA8C1',
      description: 'Scripture is the Holy authoritative, Spirit-breathed Word of God.',
      questions: [
        'How has the Holy Spirit shaped my heart as I have spent active time in the Bible this week?',
        'How have I responded to the Lord\'s moving in my study of Scripture?'
      ]
    },
    {
      key: 'serve_others',
      title: 'Serve Others',
      icon: Users,
      color: '#7BA888',
      description: 'Jesus\' followers are called to fully love God and love neighbors. This is most often shown through selflessly serving in this world.',
      questions: [
        'How have I served God with my natural and spiritual gifts in my sphere recently?',
        'How have I put people beyond the walls of the church before myself today?'
      ]
    },
    {
      key: 'radiate_jesus',
      title: 'Radiate Jesus',
      icon: Sparkles,
      color: '#D4A574',
      description: 'Part of being a disciple is fishing for people. Jesus expects us to be ready and attentive in this world so that we might intentionally radiate the good news of his life, death, resurrection and indwelling Holy Spirit. Jesus is always sending us into a broken world in need of his healing.',
      questions: [
        'Where did I proclaim or demonstrate the life changing news of Jesus Christ recently?',
        'How is Jesus working in me currently to demonstrate his good news to others?'
      ]
    }
  ];

  // Rating vital signs for weekly check-in
  const ratingVitalSigns = [
    {
      key: 'pursue_god_rating',
      title: 'Pursue God',
      icon: Heart,
      color: '#2B6B7F',
      description: 'How consistent has your prayer life been this week?'
    },
    {
      key: 'engage_scripture_rating',
      title: 'Engage Scripture',
      icon: BookOpen,
      color: '#5FA8C1',
      description: 'How actively have you engaged with God\'s Word?'
    },
    {
      key: 'serve_others_rating',
      title: 'Serve Others',
      icon: Users,
      color: '#7BA888',
      description: 'How have you served others with your gifts?'
    },
    {
      key: 'radiate_jesus_rating',
      title: 'Radiate Jesus',
      icon: Sparkles,
      color: '#D4A574',
      description: 'How have you shared Christ with others?'
    }
  ];

  // Fetch existing check-in for this week
  useEffect(() => {
    const fetchExistingCheckin = async () => {
      try {
        const response = await axios.get(`${API}/checkin/latest`);
        if (response.data && response.data.week_start_date === weekStart) {
          setRatings({
            pursue_god_rating: response.data.pursue_god_rating,
            engage_scripture_rating: response.data.engage_scripture_rating,
            serve_others_rating: response.data.serve_others_rating,
            radiate_jesus_rating: response.data.radiate_jesus_rating
          });
          setCheckinNotes(response.data.notes || '');
        }
      } catch (error) {
        // 404 means no existing check-in for this week; only log unexpected errors
        if (error?.response?.status !== 404) {
          console.error('DailyJournal: failed to fetch latest check-in', error);
        }
      } finally {
        setLoadingCheckin(false);
      }
    };
    fetchExistingCheckin();
  }, [API, weekStart]);

  const handleRatingChange = (key, value) => {
    setRatings({ ...ratings, [key]: value[0] });
  };

  const handleCheckinSubmit = async (e) => {
    e.preventDefault();
    setSavingCheckin(true);

    try {
      await axios.post(`${API}/checkin`, {
        week_start_date: weekStart,
        ...ratings,
        notes: checkinNotes || null
      });
      
      toast.success('Weekly check-in saved!');
    } catch (error) {
      toast.error('Failed to save check-in');
    } finally {
      setSavingCheckin(false);
    }
  };

  const getWeekDisplay = () => {
    const monday = new Date(weekStart);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    return `${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const handleSave = async (vitalSign) => {
    const entryText = entries[vitalSign];
    
    if (!entryText.trim()) {
      toast.error('Please write something before saving');
      return;
    }

    setSaving(vitalSign);
    
    try {
      await axios.post(`${API}/journal`, {
        vital_sign: vitalSign,
        entry_text: entryText,
        date: today
      });
      
      toast.success('Journal entry saved!');
      setEntries({ ...entries, [vitalSign]: '' });
    } catch (error) {
      toast.error('Failed to save entry');
    } finally {
      setSaving(null);
    }
  };

  const fetchPastEntries = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/journal`);
      setPastEntries(response.data);
    } catch (error) {
      toast.error('Failed to load past entries');
    } finally {
      setLoadingHistory(false);
    }
  }, [API]);

  useEffect(() => {
    if (viewMode === 'history' && pastEntries.length === 0) {
      fetchPastEntries();
    }
  }, [viewMode, pastEntries.length, fetchPastEntries]);

  // Deep-link from global search: /journal?entry=<id> flips to History mode
  // and scrolls the target entry into view once loaded.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const targetId = params.get('entry');
    if (!targetId) return;
    setViewMode('history');
    // Wait for pastEntries to appear, then scroll.
    const check = () => {
      const el = document.getElementById(`journal-entry-${targetId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.transition = 'box-shadow 0.6s ease';
        el.style.boxShadow = '0 0 0 3px #2B6B7F';
        setTimeout(() => { el.style.boxShadow = ''; }, 1600);
        // Strip the param so refresh doesn't keep re-jumping.
        const clean = new URL(window.location.href);
        clean.searchParams.delete('entry');
        window.history.replaceState({}, '', clean.toString());
        return true;
      }
      return false;
    };
    let tries = 0;
    const int = setInterval(() => {
      tries += 1;
      if (check() || tries > 20) clearInterval(int);
    }, 250);
    return () => clearInterval(int);
  }, [pastEntries.length]);

  const formatDate = (dateStr) => formatLocalDate(dateStr, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const getVitalSignInfo = (key) => {
    return vitalSigns.find(sign => sign.key === key);
  };

  const [exporting, setExporting] = useState(false);

  const handleExportJournal = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/journal/export`);
      const { filename, content } = response.data;
      
      // Create blob and download
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Journal exported successfully!');
    } catch (error) {
      toast.error('Failed to export journal');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50" data-testid="journal-page">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="mb-8 animate-fade-in">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-4xl font-heading font-bold text-[#2B6B7F]">
              Vital Signs Check
            </h1>
            <TutorialHint videoId="HuVmOQRcjhI" />
          </div>
          <p className="text-slate-600">Track your spiritual health daily and weekly</p>
        </div>

        {/* Main Tab: Weekly Check-In vs Daily Journal */}
        <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="space-y-6">
          <TabsList className="grid grid-cols-2 h-auto bg-slate-100 p-1 rounded-lg">
            <TabsTrigger 
              value="checkin" 
              className="flex items-center gap-2 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <CalendarIcon className="w-4 h-4 text-[#7BA888]" />
              <span className="font-medium">Weekly Check-In</span>
            </TabsTrigger>
            <TabsTrigger 
              value="daily" 
              className="flex items-center gap-2 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <Save className="w-4 h-4 text-[#5FA8C1]" />
              <span className="font-medium">Daily Journal</span>
            </TabsTrigger>
          </TabsList>

          {/* Weekly Check-In Tab Content */}
          <TabsContent value="checkin" className="space-y-6">
            <div className="text-center mb-4">
              <p className="text-sm text-slate-500">
                Week of {getWeekDisplay()}
              </p>
            </div>

            <form onSubmit={handleCheckinSubmit} className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl font-heading text-[#2B6B7F] flex items-center gap-2">
                    <TrendingUp className="w-6 h-6" />
                    Rate Your Week
                  </CardTitle>
                  <CardDescription>
                    Use the sliders to rate each vital sign from 1 (needs improvement) to 10 (excellent)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-8">
                  {ratingVitalSigns.map((sign) => {
                    const Icon = sign.icon;
                    const currentRating = ratings[sign.key];
                    
                    return (
                      <div key={sign.key} className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center"
                               style={{ backgroundColor: `${sign.color}20` }}>
                            <Icon className="w-5 h-5" style={{ color: sign.color }} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="font-semibold text-slate-800">{sign.title}</h3>
                              <span className="text-2xl font-bold" style={{ color: sign.color }}>
                                {currentRating}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600">{sign.description}</p>
                          </div>
                        </div>
                        <Slider
                          value={[currentRating]}
                          onValueChange={(value) => handleRatingChange(sign.key, value)}
                          min={1}
                          max={10}
                          step={1}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-slate-500">
                          <span>1 - Needs Improvement</span>
                          <span>10 - Excellent</span>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-xl font-heading text-[#2B6B7F]">
                    Weekly Notes
                  </CardTitle>
                  <CardDescription>
                    Add any reflections, prayer requests, or goals for next week
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea
                    placeholder="What was significant about this week spiritually? What are you grateful for? What do you want to focus on next week?"
                    value={checkinNotes}
                    onChange={(e) => setCheckinNotes(e.target.value)}
                    className="min-h-[120px]"
                  />
                </CardContent>
              </Card>

              <Button 
                type="submit" 
                className="w-full bg-[#7BA888] hover:bg-[#5E8A6A]"
                disabled={savingCheckin}
              >
                <Save className="w-4 h-4 mr-2" />
                {savingCheckin ? 'Saving...' : 'Save Weekly Check-In'}
              </Button>
            </form>
          </TabsContent>

          {/* Daily Journal Tab Content */}
          <TabsContent value="daily" className="space-y-6">
            <div className="text-center mb-4">
              <p className="text-sm text-slate-500">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>

            {/* Toggle between New Entry and History */}
            <div className="flex flex-wrap gap-3 mb-6">
              <Button
                onClick={() => setViewMode('new')}
                variant={viewMode === 'new' ? 'default' : 'outline'}
                className={viewMode === 'new' ? 'bg-[#2B6B7F]' : ''}
              >
                <Save className="w-4 h-4 mr-2" />
                New Entry
              </Button>
              <Button
                onClick={() => setViewMode('history')}
                variant={viewMode === 'history' ? 'default' : 'outline'}
                className={viewMode === 'history' ? 'bg-[#2B6B7F]' : ''}
              >
                <History className="w-4 h-4 mr-2" />
                View Past Entries
              </Button>
              <Button
                onClick={handleExportJournal}
                variant="outline"
                disabled={exporting}
                className="ml-auto border-green-500 text-green-700 hover:bg-green-50"
                data-testid="export-journal-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export Journal'}
              </Button>
            </div>

            {/* New Entry View */}
            {viewMode === 'new' && (
              <Tabs defaultValue="pursue_god" className="space-y-6">
                <TabsList className="grid grid-cols-2 md:grid-cols-4 h-auto gap-2 bg-transparent">
                  {vitalSigns.map((sign) => {
                    const Icon = sign.icon;
                    return (
                      <TabsTrigger
                        key={sign.key}
                        value={sign.key}
                        className="flex flex-col items-center gap-2 p-4 h-auto data-[state=active]:bg-white data-[state=active]:shadow-md"
                        data-testid={`tab-${sign.key}`}
                      >
                        <Icon className="w-5 h-5" style={{ color: sign.color }} />
                        <span className="text-xs font-medium">{sign.title}</span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

            {vitalSigns.map((sign) => {
              const Icon = sign.icon;
              return (
                <TabsContent key={sign.key} value={sign.key} className="animate-fade-in">
                  <Card className="border-t-4" style={{ borderTopColor: sign.color }}>
                    <CardHeader>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center" 
                             style={{ backgroundColor: `${sign.color}20` }}>
                          <Icon className="w-6 h-6" style={{ color: sign.color }} />
                        </div>
                        <div>
                          <CardTitle className="text-2xl font-heading" style={{ color: sign.color }}>
                            {sign.title}
                          </CardTitle>
                        </div>
                      </div>
                      <CardDescription className="space-y-3 mt-4">
                        <p className="text-slate-700 leading-relaxed">{sign.description}</p>
                        <div className="pt-2">
                          <p className="font-medium text-slate-700 mb-2">Questions to consider:</p>
                          <ul className="list-disc list-inside space-y-1 text-slate-600">
                            {sign.questions.map((q) => (
                              <li key={`${sign.key}-${q}`} className="text-sm">{q}</li>
                            ))}
                          </ul>
                        </div>
                        {/* Add Seedbed link for Engage Scripture */}
                        {sign.key === 'engage_scripture' && (
                          <div className="mt-4 space-y-3">
                            {/* Daily Devotionals Link */}
                            <div className="p-3 rounded-lg bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
                              <p className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
                                <span>☀️</span>
                                Daily Devotionals
                              </p>
                              <button
                                onClick={() => window.location.href = '/daily-devotionals'}
                                className="inline-flex items-center gap-2 text-sm font-medium text-amber-700 hover:text-amber-800"
                              >
                                <BookOpen className="w-4 h-4" />
                                Open Daily Devotionals
                                <ExternalLink className="w-3 h-3" />
                              </button>
                              <p className="text-xs text-slate-600 mt-2">
                                2-year Bible reading plan with AI-generated questions
                              </p>
                            </div>
                            <div className="p-3 rounded-lg bg-gradient-to-br from-blue-50 to-teal-50 border border-blue-200">
                              <p className="text-sm font-semibold text-slate-800 mb-2">
                                Need help getting started with Scripture?
                              </p>
                              <a
                                href="https://seedbed.com/wakeupcall/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-sm font-medium text-[#2B6B7F] hover:text-[#1E4D5F]"
                              >
                                <BookOpen className="w-4 h-4" />
                                Try Seedbed's Wake Up Call
                                <ExternalLink className="w-3 h-3" />
                              </a>
                              <p className="text-xs text-slate-600 mt-2">
                                Daily audio Bible teaching to help you engage God's Word
                              </p>
                            </div>
                            <div className="p-3 rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200">
                              <p className="text-sm font-semibold text-slate-800 mb-2">
                                Explore Bible Resources
                              </p>
                              <button
                                onClick={() => window.location.href = '/resources'}
                                className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-700"
                              >
                                <BookOpen className="w-4 h-4" />
                                Visit Bible Resources Page
                                <ExternalLink className="w-3 h-3" />
                              </button>
                              <p className="text-xs text-slate-600 mt-2">
                                Access commentaries, guides, and study tools
                              </p>
                            </div>
                          </div>
                        )}
                        {/* Add Prayer Feed for Pursue God */}
                        {sign.key === 'pursue_god' && (
                          <div className="mt-4 p-3 rounded-lg bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200">
                            <p className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
                              <span>🙏</span>
                              Pray Through Scripture
                            </p>
                            <p className="text-xs text-slate-600 mb-3">
                              Use these guided prayers to help deepen your conversation with God
                            </p>
                            <a
                              href="https://app.echoprayer.com/feed/praying-the-scriptures"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-sm font-medium text-purple-600 hover:text-purple-700 mb-3"
                            >
                              <span>Open in Full View</span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        )}
                        {/* Add Fresh Expression Resource for Radiate Jesus */}
                        {sign.key === 'radiate_jesus' && (
                          <div className="mt-4 p-3 rounded-lg bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
                            <p className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
                              <Church className="w-4 h-4 text-amber-600" />
                              Ready to Lead Others?
                            </p>
                            <p className="text-xs text-slate-600 mb-3">
                              Learn how to start a Fresh Expression of church in your community
                            </p>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  className="border-amber-400 text-amber-700 hover:bg-amber-100 flex items-center gap-2"
                                  size="sm"
                                >
                                  <Church className="w-4 h-4" />
                                  Resource for Beginning a Fresh Expression
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle className="text-2xl font-heading text-amber-700 flex items-center gap-2">
                                    <Church className="w-6 h-6" />
                                    You Can Lead a Fresh Expression of Church
                                  </DialogTitle>
                                  <DialogDescription className="text-slate-600">
                                    A simple guide to starting meaningful spiritual gatherings
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-6 mt-4">
                                  {/* Step 1 */}
                                  <div className="p-4 rounded-lg bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200">
                                    <h3 className="font-bold text-lg text-rose-700 mb-2 flex items-center gap-2">
                                      <span className="w-8 h-8 rounded-full bg-rose-600 text-white flex items-center justify-center text-sm font-bold">1</span>
                                      Connect People Over Food and/or Drinks and Intentional Conversation
                                    </h3>
                                    <p className="text-slate-700 mb-3">Use one of the following…</p>
                                    <ul className="space-y-2 text-slate-600">
                                      <li className="flex items-start gap-2">
                                        <span className="text-rose-500">•</span>
                                        <span><strong>SOULARIUM cards</strong></span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <span className="text-rose-500">•</span>
                                        <span><strong>Deep Meaningful Conversations</strong> deck of cards</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <span className="text-rose-500">•</span>
                                        <span>Questions from <strong>Table Talk Card</strong></span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <span className="text-rose-500">•</span>
                                        <span>Something else the Spirit may be drawing you to</span>
                                      </li>
                                    </ul>
                                  </div>

                                  {/* Step 2 */}
                                  <div className="p-4 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
                                    <h3 className="font-bold text-lg text-blue-700 mb-2 flex items-center gap-2">
                                      <span className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">2</span>
                                      Center People Towards God
                                    </h3>
                                    <ul className="space-y-2 text-slate-600">
                                      <li className="flex items-start gap-2">
                                        <span className="text-blue-500">•</span>
                                        <span>You can listen to a worship song online</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <span className="text-blue-500">•</span>
                                        <span>You can sing or play a song</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <span className="text-blue-500">•</span>
                                        <span>You can read a Psalm</span>
                                      </li>
                                    </ul>
                                  </div>

                                  {/* Step 3 */}
                                  <div className="p-4 rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200">
                                    <h3 className="font-bold text-lg text-emerald-700 mb-2 flex items-center gap-2">
                                      <span className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center text-sm font-bold">3</span>
                                      Have Jesus Conversations
                                    </h3>
                                    <p className="text-slate-700 mb-3">Use one of the following…</p>
                                    <ul className="space-y-2 text-slate-600">
                                      <li className="flex items-start gap-2">
                                        <span className="text-emerald-500">•</span>
                                        <span><strong>Study a teaching of Jesus together</strong></span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <span className="text-emerald-500">•</span>
                                        <span>Study passages on specific topics where you, as a group, can really understand God's heart around things like <em>faith, love, suffering, reasoning</em>, and so much more.</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <span className="text-emerald-500">•</span>
                                        <span>Share struggles and invite Jesus, the one who weeps with us, to be present in each life.</span>
                                      </li>
                                    </ul>
                                  </div>

                                  {/* Step 4 */}
                                  <div className="p-4 rounded-lg bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200">
                                    <h3 className="font-bold text-lg text-purple-700 mb-2 flex items-center gap-2">
                                      <span className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold">4</span>
                                      Pray
                                    </h3>
                                    <p className="text-slate-600">
                                      Close your time together by lifting up prayers for one another, your community, and the world.
                                    </p>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </div>
                        )}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <Textarea
                        placeholder="Write your reflections here..."
                        value={entries[sign.key]}
                        onChange={(e) => setEntries({ ...entries, [sign.key]: e.target.value })}
                        className="min-h-[200px] resize-none"
                        data-testid={`textarea-${sign.key}`}
                      />
                      <Button
                        onClick={() => handleSave(sign.key)}
                        disabled={saving === sign.key}
                        className="w-full h-12"
                        style={{ backgroundColor: sign.color }}
                        data-testid={`save-button-${sign.key}`}
                      >
                        <Save className="w-5 h-5 mr-2" />
                        {saving === sign.key ? 'Saving...' : 'Save Entry'}
                      </Button>

                      {/* Embedded Prayer Feed for Pursue God */}
                      {sign.key === 'pursue_god' && (
                        <div className="mt-6 border-t-4 border-purple-500 rounded-lg overflow-hidden">
                          <div className="bg-gradient-to-r from-purple-500 to-blue-500 p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 className="text-white font-semibold flex items-center gap-2">
                                  <span>📖</span>
                                  Pray the Scriptures
                                </h3>
                                <p className="text-white/90 text-sm">A tool for praying through Scripture</p>
                              </div>
                              <a
                                href="https://app.echoprayer.com/feed/praying-the-scriptures"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white/90 hover:text-white flex items-center gap-1 text-sm"
                              >
                                <span className="hidden sm:inline">Full View</span>
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </div>
                          </div>
                          <div className="bg-white">
                            <iframe
                              src="https://app.echoprayer.com/feed/praying-the-scriptures"
                              title="Pray the Scriptures"
                              className="w-full border-0"
                              style={{ height: '500px' }}
                              allow="clipboard-write"
                              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                            />
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              );
            })}
          </Tabs>
        )}

        {/* History View */}
        {viewMode === 'history' && (
          <div className="space-y-4">
            {loadingHistory ? (
              <Card>
                <CardContent className="pt-6 text-center py-12">
                  <div className="text-lg text-slate-600">Loading past entries...</div>
                </CardContent>
              </Card>
            ) : pastEntries.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center py-12">
                  <History className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">
                    No Past Entries Yet
                  </h3>
                  <p className="text-slate-600">
                    Start journaling to see your spiritual growth over time
                  </p>
                </CardContent>
              </Card>
            ) : (
              pastEntries.map((entry) => {
                const signInfo = getVitalSignInfo(entry.vital_sign);
                const Icon = signInfo?.icon || BookOpen;
                const color = signInfo?.color || '#2B6B7F';
                
                return (
                  <Card
                    key={entry.id}
                    id={`journal-entry-${entry.id}`}
                    className="border-l-4"
                    style={{ borderLeftColor: color }}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center" 
                               style={{ backgroundColor: `${color}20` }}>
                            <Icon className="w-5 h-5" style={{ color }} />
                          </div>
                          <div>
                            <CardTitle className="text-lg" style={{ color }}>
                              {signInfo?.title || entry.vital_sign}
                            </CardTitle>
                            <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                              <CalendarIcon className="w-4 h-4" />
                              {formatDate(entry.date)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                        {entry.entry_text}
                      </p>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DailyJournal;
